import React, { useState, useEffect, useCallback } from 'react';
import { Tree } from 'react-arborist';
import API from '../services/API';
import SystemHeader from './SystemHeader';
import CanvasOrgChart from './CanvasOrgChart';

const TeamList = ({ userInfo, onLogout, totalPoints = 0, onRefreshPoints }) => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [teamInfo, setTeamInfo] = useState(null);
  const [viewMode, setViewMode] = useState('arborist'); // 'grid', 'arborist', or 'canvas'

  const [treeData, setTreeData] = useState([]);
  const [treeRef, setTreeRef] = useState(null);

  const fetchTeamMembers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      if (!userInfo || !userInfo.phoneNumber) {
        throw new Error('Không có thông tin người dùng');
      }

      console.log('🚀 Fetching team members for user:', userInfo.phoneNumber);
      
      // Gọi API để lấy danh sách thành viên trong nhóm với cấu trúc cây mới
      const result = await API.getSubordinates(userInfo.phoneNumber);
      
      if (result.success && (result.subordinates || result.hierarchy)) {
        // Helper function to count direct subordinates (F1)
        const countDirectSubordinates = (memberId, allMembers) => {
          return allMembers.filter(m => m.parentId === memberId || m.phoneParent === memberId).length;
        };
        
        // Map flattened API data to component format for grid view
        const mappedMembers = (result.subordinates || []).map((member, index) => ({
          id: member.id || index + 1,
          userName: member.userName,
          name: member.name || 'Không có tên',
          firstName: member.firstName,
          lastName: member.lastName,
          phone: member.phone,
          email: member.email,
          address: member.address,
          status: getStatusFromData(member),
          level: member.level || 1,
          avatar: member.avatar || getDefaultAvatar(member.name),
          facebook: member.facebook,
          birthday: member.birthday,
          phoneParent: member.phoneParent,
          nameParent: member.nameParent,
          teamLeader: member.teamLeader,
          city: member.city,
          district: member.district,
          ward: member.ward,
          organizationId: member.organizationId,
          parentId: member.parentId,
          roleId: member.roleId,
          createdAt: member.createdAt,
          updatedAt: member.updatedAt,
          joinDate: member.createdAt || new Date().toISOString(),
          f1Count: countDirectSubordinates(member.phone || member.id, result.subordinates || [])
        }));
        
        // Process hierarchy data for tree view
        const processTreeData = (nodes) => {
          return nodes.map(node => ({
            id: node.id?.toString() || node.phone || Date.now().toString(),
            name: node.name || 'Không có tên',
            phone: node.phone,
            userName: node.userName,
            email: node.email,
            level: node.level,
            avatar: node.avatar || getDefaultAvatar(node.name),
            phoneParent: node.phoneParent,
            nameParent: node.nameParent,
            teamLeader: node.teamLeader,
            city: node.city,
            status: getStatusFromData(node),
            createdAt: node.createdAt,
            children: node.children ? processTreeData(node.children) : []
          }));
        };

        const hierarchyData = result.hierarchy ? processTreeData(result.hierarchy) : [];
        
        // Add root node (current user) to tree data
        const rootNode = {
          id: 'root_' + userInfo.phoneNumber,
          name: userInfo.fullName || userInfo.phoneNumber,
          phone: userInfo.phoneNumber,
          userName: userInfo.phoneNumber,
          email: userInfo.email || null,
          level: 1,
          avatar: userInfo.avatar || getDefaultAvatar(userInfo.fullName || userInfo.phoneNumber),
          phoneParent: null,
          nameParent: null,
          teamLeader: null,
          city: userInfo.city || null,
          status: 'active',
          createdAt: new Date().toISOString(),
          children: hierarchyData,
          isOpen: true // Mặc định mở node gốc
        };
        
        const treeWithRoot = [rootNode];
        
        console.log('✅ Team members loaded successfully:', { 
          flatMembers: mappedMembers.length, 
          hierarchyNodes: hierarchyData.length,
          treeWithRoot: treeWithRoot.length
        });
        
        setTeamMembers(mappedMembers);
        setTreeData(treeWithRoot);
        setTeamInfo({
          total: result.total,
          userCode: result.userInfo?.userCode,
          userName: result.userInfo?.userName,
          level: result.userInfo?.level,
          totalDescendants: result.userInfo?.totalDescendants
        });
      } else {
        console.log('⚠️ No team members found for user');
        setTeamMembers([]);
        
        // Still create a root node for the tree even if there are no subordinates
        const rootNode = {
          id: 'root_' + userInfo.phoneNumber,
          name: userInfo.fullName || userInfo.phoneNumber,
          phone: userInfo.phoneNumber,
          userName: userInfo.phoneNumber,
          email: userInfo.email || null,
          level: 1,
          avatar: userInfo.avatar || getDefaultAvatar(userInfo.fullName || userInfo.phoneNumber),
          phoneParent: null,
          nameParent: null,
          teamLeader: null,
          city: userInfo.city || null,
          status: 'active',
          createdAt: new Date().toISOString(),
          children: [],
          isOpen: true // Mặc định mở node gốc
        };
        
        setTreeData([rootNode]);
        setTeamInfo({ total: 0 });
      }
      
      setLoading(false);
    } catch (error) {
      console.error('❌ Error fetching team members:', error);
      setError(error.message || 'Không thể tải danh sách thành viên');
      setLoading(false);
    }
  }, [userInfo]);

  useEffect(() => {
    if (userInfo && userInfo.phoneNumber) {
      fetchTeamMembers();
    }
  }, [userInfo, fetchTeamMembers]);

  // Effect to open root node after tree is mounted
  useEffect(() => {
    if (treeRef && treeData.length > 0 && viewMode === 'arborist') {
      // Delay to ensure tree is fully rendered
      setTimeout(() => {
        const rootNodeId = treeData[0]?.id;
        if (rootNodeId) {
          try {
            // Find the root node and open it
            const rootNode = treeRef.get(rootNodeId);
            if (rootNode && !rootNode.isOpen) {
              rootNode.open();
            }
          } catch (error) {
            console.log('Unable to open root node:', error);
          }
        }
      }, 100);
    }
  }, [treeRef, treeData, viewMode]);

  // Helper function to determine status from API data
  const getStatusFromData = (member) => {
    if (member.status === 2) {
      return 'active';
    }
    if (member.status === 1) {
      return 'inactive';
    }
    return 'pending';
  };

  // Helper function to get default avatar
  const getDefaultAvatar = (name) => {
    const avatars = {
      'A': 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      'B': 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      'C': 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      'D': 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face',
      'E': 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face'
    };
    
    const firstLetter = name ? name.charAt(0).toUpperCase() : 'A';
    return avatars[firstLetter] || avatars['A'];
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'Chưa có';
    return phone.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3');
  };

  const handleMemberClick = (member) => {
    // Có thể mở modal chi tiết thành viên hoặc navigate đến trang profile
    console.log('Member clicked:', member);
  };



  // Tree Node Component for react-arborist
  const ArboristNode = ({ node, style, dragHandle, tree }) => {
    const isInternal = node.isInternal;
    const hasChildren = node.children && node.children.length > 0;
    
    return (
      <div 
        style={style} 
        ref={dragHandle}
        className={`flex items-center py-2 px-3 hover:bg-gray-50 rounded cursor-pointer ${
          node.isSelected ? 'bg-brand-50 border-l-4 border-brand-500' : ''
        }`}
        onClick={() => tree.select(node)}
      >
        {/* Expand/Collapse Button */}
        {isInternal && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              node.toggle();
            }}
            className="mr-2 p-1 hover:bg-gray-200 rounded"
          >
            {node.isOpen ? (
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            ) : (
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
          </button>
        )}
        
        {/* Indent for hierarchy */}
        <div style={{ marginLeft: `${node.level * 20}px` }} className="flex items-center space-x-3 flex-1">
          {/* Avatar */}
          <div className="relative">
            <img 
              src={node.data.avatar} 
              alt={node.data.name}
              className="w-8 h-8 rounded-full object-cover"
              onError={(e) => {
                e.target.src = getDefaultAvatar(node.data.name);
              }}
            />
            {node.data.status === 'active' && (
              <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
            )}
          </div>
          
          {/* Member Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <span className="font-medium text-gray-900 truncate">
                {node.data.name}
              </span>
              {hasChildren && (
                <span className="text-xs bg-brand-100 text-brand-800 px-2 py-1 rounded-full">
                  {node.children.length}
                </span>
              )}
            </div>
            <div className="text-sm text-gray-500 truncate">
              {formatPhoneNumber(node.data.phone)}
              {node.data.city && ` • ${node.data.city}`}
            </div>
          </div>
          
          {/* Level Badge */}
          <div className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
            Level {node.data.level}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Đang tải danh sách thành viên...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <p className="text-red-600 font-medium">{error}</p>
          <button 
            onClick={fetchTeamMembers}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Thử lại
          </button>
        </div>
      </div>
    );
  }

  // Calculate total team info for header
  const totalMembers = teamMembers.length;

  // Extra info for header - team stats
  const extraInfo = (
    <div className="text-center">
      <p className="text-lg font-semibold text-gray-900">{totalMembers} Thành viên</p>
      <p className="text-sm text-gray-500">Tổng số thành viên trong nhóm</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 to-brand-100">
      {/* System Header */}
      <SystemHeader
        userInfo={userInfo}
        onLogout={onLogout}
        totalPoints={totalPoints}
        pageTitle="Tổ chức của tôi"
        pageSubtitle="Quản lý thành viên trong nhóm"
        extraInfo={extraInfo}
        onRefreshPoints={onRefreshPoints}
      />

      {/* Team Members Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Team Stats Summary */}
        {teamInfo && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-brand-600">{teamInfo.total || 0}</div>
              <div className="text-lg text-gray-500">Tổng thành viên</div>
            </div>
          </div>
        )}

        {/* View Mode Switch */}
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-lg p-1 shadow-md">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-4 py-2 rounded-md font-medium transition-all duration-200 ${
                viewMode === 'grid'
                  ? 'bg-brand-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
                <span>Danh sách</span>
              </div>
            </button>
            <button
              onClick={() => setViewMode('arborist')}
              className={`px-4 py-2 rounded-md font-medium transition-all duration-200 ${
                viewMode === 'arborist'
                  ? 'bg-brand-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V6a2 2 0 012-2h2a2 2 0 012 2v2m0 0V6a2 2 0 012-2h2a2 2 0 012 2v2m0 0v8a2 2 0 01-2 2H6a2 2 0 01-2-2V8" />
                </svg>
                <span>Cấu trúc</span>
              </div>
            </button>
            <button
              onClick={() => setViewMode('canvas')}
              className={`px-4 py-2 rounded-md font-medium transition-all duration-200 ${
                viewMode === 'canvas'
                  ? 'bg-brand-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>Sơ đồ</span>
              </div>
            </button>
          </div>
        </div>

        {/* Table View */}
        {viewMode === 'grid' && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Họ tên
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      F1
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {teamMembers.map((member, index) => (
                    <tr 
                      key={member.id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                      onClick={() => handleMemberClick(member)}
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {member.id}-{member.name}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-brand-100 text-brand-800">
                          {member.f1Count || 0}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Empty state for table */}
              {teamMembers.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Chưa có thành viên nào</h3>
                  <p className="text-gray-500">Hiện tại bạn chưa có thành viên nào trong nhóm của mình.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Canvas Organizational Chart View */}
        {viewMode === 'canvas' && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Sơ đồ tổ chức</h3>
              <p className="text-sm text-gray-500">
                Hiển thị sơ đồ dưới dạng image với khả năng zoom và pan tự do
                {teamInfo && teamInfo.totalDescendants && (
                  <span className="ml-2 font-medium text-brand-600">
                    • {teamInfo.totalDescendants} thành viên
                  </span>
                )}
              </p>
            </div>
            
            <div className="p-6">
              <CanvasOrgChart 
                treeData={treeData}
                loading={loading}
                onNodeClick={handleMemberClick}
              />
            </div>
          </div>
        )}

        {/* High-Performance Tree View */}
        {viewMode === 'arborist' && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Cấu trúc tổ chức</h3>
              <p className="text-sm text-gray-500">
                Hiển thị cấu trúc phân cấp với khả năng ảo hóa cho hiệu suất tối ưu
                {teamInfo && teamInfo.totalDescendants && (
                  <span className="ml-2 font-medium text-brand-600">
                    • {teamInfo.totalDescendants} thành viên
                  </span>
                )}
              </p>
            </div>
            
            {treeData.length > 0 ? (
              <div style={{ height: '600px' }}>
                <Tree
                  ref={setTreeRef}
                  data={treeData}
                  openByDefault={false}
                  width="100%"
                  height={600}
                  rowHeight={64}
                  indent={24}
                  className="tree-container"
                  initialOpenState={{[treeData[0]?.id]: true}}
                >
                  {ArboristNode}
                </Tree>
              </div>
            ) : (
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V6a2 2 0 012-2h2a2 2 0 012 2v2m0 0V6a2 2 0 012-2h2a2 2 0 012 2v2m0 0v8a2 2 0 01-2 2H6a2 2 0 01-2-2V8" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Đang tải cây tổ chức...</h3>
                <p className="text-gray-500">Hệ thống đang xử lý dữ liệu phân cấp</p>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default TeamList;
